import { useMemo } from 'react';
import { IEX_STATE_CODES } from '@constants/configs/states';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import { useAppSelector } from '../utils';

/**
* Pure function that maps a role + state to permission flags.
* No Redux, no hooks — can be called directly in unit tests.
*/
export const getRolePermissions = (currentRole: string, stateCode: IEX_STATE_CODES) => {
    const hasRole = (roles: readonly string[]) => roles.includes(currentRole);
    const isState = (code: IEX_STATE_CODES) => stateCode === code;

    const isBrokerAdmin = hasRole([ALL_USER_ROLES.BROKER_ADMIN]);

    return {
        canExportCapHistory: !(isState(IEX_STATE_CODES.VA) && isBrokerAdmin),
    };
};

/**
* Connected hook that reads the active role and state code from Redux
* and delegates to {@link getRolePermissions}. Memoized on role/state change.
*/
export const useRolePermission = () => {
    const environment = useAppSelector(state => state?.common?.environment);
    const activeRoleName: string = environment?.user?.activeModuleName?.toUpperCase();
    const stateCode = environment?.stateCode as IEX_STATE_CODES;

    const permissions = useMemo(
        () => getRolePermissions(activeRoleName, stateCode),
        [activeRoleName, stateCode],
    );

    return permissions;
};

 